package features.members.handlers

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.html.dom.append
import kotlinx.html.div
import kotlinx.html.p
import kotlinx.html.span
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.files.FileReader
import kotlin.js.Promise
import features.members.domain.Member
import shared.utils.CSVUtils
import shared.utils.ParseResult
import shared.ui.ModalManager

/**
 * Member Management Functions
 * Handles CSV import, bulk operations, search/filter
 */

// Global state for import
var importPreviewData: ParseResult? = null

/**
 * Handle CSV file selection and preview
 */
fun handleCSVFileSelect() {
    val fileInput = document.getElementById("csv-file-input") as? HTMLInputElement
    val files = fileInput?.asDynamic().files
    val file = if (files != null && files.length > 0) files[0] else null
    
    if (file == null) {
        console.log("No file selected")
        return
    }
    
    console.log("📄 Reading CSV file: ${file.name}")
    
    val reader = FileReader()
    reader.onload = { event ->
        val csvText = event.target.asDynamic().result as String
        console.log("CSV content length: ${csvText.length}")
        
        // Parse CSV
        val parseResult = CSVUtils.parseCSV(csvText)
        importPreviewData = parseResult
        
        console.log("✅ Valid members: ${parseResult.validMembers.size}")
        console.log("❌ Errors: ${parseResult.errors.size}")
        
        // Update UI
        updateImportPreview(parseResult)
    }
    
    reader.readAsText(file)
}

/**
 * Update import preview section
 */
fun updateImportPreview(result: ParseResult) {
    val previewSection = document.getElementById("import-preview-section") as? HTMLElement ?: return
    val validBadge = document.getElementById("valid-count-badge") as? HTMLElement
    val errorBadge = document.getElementById("error-count-badge") as? HTMLElement
    val importBtn = document.getElementById("import-confirm-btn") as? HTMLElement
    
    // Update badges
    validBadge?.textContent = "${result.validMembers.size} valid"
    errorBadge?.textContent = "${result.errors.size} errors"
    
    // Enable/disable import button
    if (result.validMembers.isNotEmpty()) {
        importBtn?.removeAttribute("disabled")
    } else {
        importBtn?.setAttribute("disabled", "true")
    }
    
    // Clear and rebuild preview
    previewSection.innerHTML = ""
    previewSection.append {
        if (result.validMembers.isNotEmpty()) {
            div("mb-4") {
                p("text-sm font-bold text-emerald-400 mb-2") { +"✅ Valid Members (${result.validMembers.size})" }
                div("bg-dark-900 rounded-xl p-4 max-h-64 overflow-y-auto custom-scrollbar") {
                    result.validMembers.take(10).forEach { member ->
                        div("flex justify-between items-center py-2 border-b border-dark-700 last:border-0") {
                            div {
                                span("font-bold text-white") { +member.name }
                                span("text-slate-400 text-sm ml-2") { +"(${member.email})" }
                            }
                            span("px-2 py-1 rounded bg-brand-cyan/20 text-brand-cyan text-xs") {
                                +member.role
                            }
                        }
                    }
                    if (result.validMembers.size > 10) {
                        p("text-slate-500 text-sm mt-2") { +"... and ${result.validMembers.size - 10} more" }
                    }
                }
            }
        }
        
        if (result.errors.isNotEmpty()) {
            div {
                p("text-sm font-bold text-red-400 mb-2") { +"❌ Errors (${result.errors.size})" }
                div("bg-red-500/5 border border-red-500/20 rounded-xl p-4 max-h-48 overflow-y-auto custom-scrollbar") {
                    result.errors.forEach { error ->
                        p("text-sm text-red-400 mb-1") { +error }
                    }
                }
            }
        }
    }
}

/**
 * Import valid members to Firestore
 */
fun importValidMembers() {
    val result = importPreviewData ?: return
    val teamId = AppState.currentTeam?.id ?: return
    
    if (result.validMembers.isEmpty()) {
        window.alert("No valid members to import")
        return
    }
    
    console.log("📦 Importing ${result.validMembers.size} members...")
    
    features.members.data.MemberRepository.bulkAddMembers(teamId, result.validMembers)
        .then {
            console.log("✅ Import successful")
            
            // Reload members
            features.members.data.MemberRepository.loadMembers(teamId)
                .then { members ->
                    AppState.members[teamId] = members.toMutableList()
                    importPreviewData = null
                    ModalManager.close()
                    Router.render()
                    window.alert("Successfully imported ${result.validMembers.size} members!")
                }
        }
        .catch { error ->
            console.error("❌ Import failed:", error)
            window.alert("Failed to import members: ${error.message}")
        }
}

/**
 * Export members to CSV
 */
fun exportMembersToCSV(members: List<Member>) {
    console.log("Exporting ${members.size} members to CSV")
    
    // Create CSV header (removed joinDate and quota)
    val header = "name,email,role"
    
    // Create CSV rows
    val rows = members.map { member ->
        "${member.name},${member.email},${member.role}"
    }
    
    // Combine header and rows
    val csvContent = listOf(header) + rows
    val csvString = csvContent.joinToString("\n")
    
    // Create blob and download
    val blob = js("new Blob([csvString], { type: 'text/csv;charset=utf-8;' })")
    val url = js("URL.createObjectURL(blob)")
    val link = document.createElement("a") as org.w3c.dom.HTMLAnchorElement
    link.href = url as String
    link.download = "members_export.csv"
    link.click()
    js("URL.revokeObjectURL(url)")
    
    console.log("CSV export complete")
}

/**
 * Download CSV template
 */
fun downloadCSVTemplate() {
    val csv = CSVUtils.generateTemplate()
    val blob = js("new Blob([csv], { type: 'text/csv' })")
    val url = js("URL.createObjectURL(blob)") as String
    
    val a = document.createElement("a") as org.w3c.dom.HTMLAnchorElement
    a.href = url
    a.download = "member_template.csv"
    a.click()
    
    js("URL.revokeObjectURL(url)")
    console.log("📥 Template downloaded")
}

/**
 * Show bulk edit role modal
 */
fun showBulkEditRoleModal() {
    val selectedCount = AppState.selectedMemberIds.size
    if (selectedCount == 0) {
        window.alert("Please select members first")
        return
    }
    
    val countEl = document.getElementById("bulk-edit-count") as? HTMLElement
    countEl?.textContent = "Change role for $selectedCount selected member${if (selectedCount > 1) "s" else ""}"
    
    ModalManager.show("bulk-edit-role-modal")
}

/**
 * Apply bulk role edit
 */
fun applyBulkRoleEdit() {
    val teamId = AppState.currentTeam?.id ?: return
    val memberIds = AppState.selectedMemberIds.toList()
    val newRole = (document.getElementById("bulk-role-select") as? HTMLSelectElement)?.value ?: return
    
    console.log("📝 Updating ${memberIds.size} members to role: $newRole")
    
    features.members.data.MemberRepository.bulkUpdateMemberRole(teamId, memberIds, newRole)
        .then {
            console.log("✅ Bulk update successful")
            
            // Reload members
            features.members.data.MemberRepository.loadMembers(teamId)
                .then { members ->
                    AppState.members[teamId] = members.toMutableList()
                    AppState.selectedMemberIds.clear()
                    ModalManager.close()
                    Router.render()
                }
        }
        .catch { error ->
            console.error("❌ Bulk update failed:", error)
            window.alert("Failed to update members: ${error.message}")
        }
}

/**
 * Show bulk delete modal
 */
fun showBulkDeleteModal() {
    val selectedCount = AppState.selectedMemberIds.size
    if (selectedCount == 0) {
        window.alert("Please select members first")
        return
    }
    
    val teamId = AppState.currentTeam?.id ?: return
    val members = AppState.members[teamId] ?: emptyList()
    val selectedMembers = members.filter { it.id in AppState.selectedMemberIds }
    
    val countEl = document.getElementById("bulk-delete-count") as? HTMLElement
    countEl?.textContent = "You are about to delete $selectedCount member${if (selectedCount > 1) "s" else ""}"
    
    val listEl = document.getElementById("bulk-delete-list") as? HTMLElement
    listEl?.innerHTML = ""
    listEl?.append {
        selectedMembers.forEach { member ->
            div("py-2 border-b border-dark-700 last:border-0") {
                span("font-bold text-white") { +member.name }
                span("text-slate-400 text-sm ml-2") { +"(${member.email})" }
            }
        }
    }
    
    ModalManager.show("bulk-delete-modal")
}

/**
 * Confirm bulk delete
 */
fun confirmBulkDelete() {
    val teamId = AppState.currentTeam?.id ?: return
    val memberIds = AppState.selectedMemberIds.toList()
    
    console.log("🗑️ Deleting ${memberIds.size} members")
    
    features.members.data.MemberRepository.bulkDeleteMembers(teamId, memberIds)
        .then {
            console.log("✅ Bulk delete successful")
            
            // Reload members
            features.members.data.MemberRepository.loadMembers(teamId)
                .then { members ->
                    AppState.members[teamId] = members.toMutableList()
                    AppState.selectedMemberIds.clear()
                    ModalManager.close()
                    Router.render()
                }
        }
        .catch { error ->
            console.error("❌ Bulk delete failed:", error)
            window.alert("Failed to delete members: ${error.message}")
        }
}

/**
 * Toggle member selection
 */
fun toggleMemberSelection(memberId: String) {
    if (AppState.selectedMemberIds.contains(memberId)) {
        AppState.selectedMemberIds.remove(memberId)
    } else {
        AppState.selectedMemberIds.add(memberId)
    }
    Router.render()
}

/**
 * Toggle select all members
 */
fun toggleSelectAllMembers() {
    val teamId = AppState.currentTeam?.id ?: return
    val members = AppState.members[teamId] ?: emptyList()
    
    if (AppState.selectedMemberIds.size == members.size) {
        // Deselect all
        AppState.selectedMemberIds.clear()
    } else {
        // Select all
        AppState.selectedMemberIds.clear()
        members.forEach { AppState.selectedMemberIds.add(it.id) }
    }
    Router.render()
}

/**
 * Filter and sort members
 */
fun filterAndSortMembers(members: List<Member>): List<Member> {
    console.log("🔍 filterAndSortMembers called with", members.size, "members")
    console.log("  memberFilterRole:", AppState.memberFilterRole)
    console.log("  memberSearchQuery:", AppState.memberSearchQuery)
    console.log("  memberSortBy:", AppState.memberSortBy)
    
    var filtered = members
    
    // Apply search filter
    if (AppState.memberSearchQuery.isNotBlank()) {
        val query = AppState.memberSearchQuery.lowercase()
        filtered = filtered.filter {
            it.name.lowercase().contains(query) || it.email.lowercase().contains(query)
        }
        console.log("  After search filter:", filtered.size)
    }
    
    // Apply role filter (case-insensitive)
    if (AppState.memberFilterRole != "All") {
        console.log("  Applying role filter:", AppState.memberFilterRole)
        val filterRole = AppState.memberFilterRole.lowercase()
        console.log("  Filter role lowercase:", filterRole)
        filtered = filtered.filter { 
            console.log("    Member:", it.name, "role:", it.role, "lowercase:", it.role.lowercase())
            it.role.lowercase() == filterRole 
        }
        console.log("  After role filter:", filtered.size)
    } else {
        console.log("  Skipping role filter (All)")
    }
    
    // Apply sorting
    filtered = when (AppState.memberSortBy) {
        "name-asc" -> filtered.sortedBy { it.name.lowercase() }
        "name-desc" -> filtered.sortedByDescending { it.name.lowercase() }
        "role" -> filtered.sortedBy { it.role }
        "joinDate" -> filtered.sortedBy { it.joinDate ?: "" }
        else -> filtered
    }
    
    console.log("  Final filtered count:", filtered.size)
    return filtered
}
